import { useEffect, useState } from "react";
import { useAuthStore } from "../../state/auth";
import { useTranslation } from "../../i18n";

export function SettingsPage() {
  const { t } = useTranslation();
  const { deviceId, user } = useAuthStore();
  const [locationAllowed, setLocationAllowed] = useState<boolean | null>(null);
  const [cameraAllowed, setCameraAllowed] = useState<boolean | null>(null);
  const [profile, setProfile] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    phone: ""
  });
  const [preferences, setPreferences] = useState({
    alerts: true,
    summaries: false
  });
  const [profileSaved, setProfileSaved] = useState<string | null>(null);
  const [passwords, setPasswords] = useState({ current: "", new: "", confirm: "" });
  const [pwStatus, setPwStatus] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    async function checkPermissions() {
      if (navigator.permissions) {
        try {
          const geo = await navigator.permissions.query({ name: "geolocation" });
          setLocationAllowed(geo.state === "granted");
          geo.onchange = () => setLocationAllowed(geo.state === "granted");
          
        } catch {
          setLocationAllowed(null);
        }
        try {
          const camera = await navigator.permissions.query({ name: "camera" as PermissionName });
          setCameraAllowed(camera.state === "granted");
          camera.onchange = () => setCameraAllowed(camera.state === "granted");
        } catch {
          setCameraAllowed(null);
        }
      }
    }
    void checkPermissions();
  }, []);


  return (
    <section className="space-y-6 pb-8 min-h-0">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900">{t("settings.title", "Account & device settings")}</h1>
        <p className="text-sm text-gray-600">
          {t("settings.subtitle", "Update your profile details and review device permissions supervisors rely on.")}
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        <form
          className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
          onSubmit={(event) => {
            event.preventDefault();
            setProfileSaved(t("settings.profileSavedMsg", "Profile preferences saved. Changes take effect immediately."));
            window.setTimeout(() => setProfileSaved(null), 3000);
          }}
        >
          <h2 className="text-lg font-semibold text-gray-900">{t("settings.profileDetails", "Profile details")}</h2>
          <div className="grid gap-4">
            <label className="text-sm text-gray-700">
              {t("settings.fullName", "Full name")}
              <input
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              />
            </label>
            <label className="text-sm text-gray-700">
              {t("settings.email", "Email")}
              <input
                type="email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              />
            </label>
            <label className="text-sm text-gray-700">
              {t("settings.mobilePhone", "Mobile phone")}
              <input
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </label>
          </div>
          {profileSaved && <p className="text-xs text-emerald-600">{profileSaved}</p>}
          <button
            type="submit"
            className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            {t("settings.saveProfile", "Save profile")}
          </button>
        </form>

        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">{t("settings.notificationPreferences", "Notification preferences")}</h2>
          <p className="text-sm text-gray-600">{t("settings.notificationDesc", "Control what alerts the supervisor team can send to you.")}</p>
          <label className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <span className="text-sm text-gray-800">{t("settings.escalationAlerts", "Escalation alerts")}</span>
            <input
              type="checkbox"
              checked={preferences.alerts}
              onChange={(e) => setPreferences((prev) => ({ ...prev, alerts: e.target.checked }))}
            />
          </label>
          <label className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <span className="text-sm text-gray-800">{t("settings.weeklyEmailSummary", "Weekly email summary")}</span>
            <input
              type="checkbox"
              checked={preferences.summaries}
              onChange={(e) => setPreferences((prev) => ({ ...prev, summaries: e.target.checked }))}
            />
          </label>
          <p className="text-xs text-gray-500">{t("settings.storedLocally", "Settings are stored locally until profile sync is available.")}</p>
        </div>
      </div>

      <form
        className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setPwStatus(null);
          if (passwords.new !== passwords.confirm) {
            setPwStatus({ type: "error", msg: "New passwords do not match." });
            return;
          }
          if (passwords.new.length < 10) {
            setPwStatus({ type: "error", msg: "New password must be at least 10 characters." });
            return;
          }
          setPwLoading(true);
          try {
            const resp = await fetch("/api/guards/change-password", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: user?.email,
                current_password: passwords.current,
                new_password: passwords.new,
              }),
            });
            const data = await resp.json();
            if (resp.ok) {
              setPwStatus({ type: "success", msg: "Password updated successfully." });
              setPasswords({ current: "", new: "", confirm: "" });
            } else {
              setPwStatus({ type: "error", msg: data.error || "Failed to update password." });
            }
          } catch {
            setPwStatus({ type: "error", msg: "Network error. Please try again." });
          } finally {
            setPwLoading(false);
          }
        }}
      >
        <h2 className="text-lg font-semibold text-gray-900">Change password</h2>
        <p className="text-sm text-gray-600">Update the password for your own account. You must enter your current password to confirm. New passwords need at least 10 characters.</p>
        <div className="grid gap-4">
          <label className="text-sm text-gray-700">
            Current password
            <input
              type="password"
              required
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={passwords.current}
              onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
            />
          </label>
          <label className="text-sm text-gray-700">
            New password
            <input
              type="password"
              required
              minLength={10}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={passwords.new}
              onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
            />
          </label>
          <label className="text-sm text-gray-700">
            Confirm new password
            <input
              type="password"
              required
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={passwords.confirm}
              onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
            />
          </label>
        </div>
        {pwStatus && (
          <p className={`text-xs ${pwStatus.type === "success" ? "text-emerald-600" : "text-red-600"}`}>
            {pwStatus.msg}
          </p>
        )}
        <button
          type="submit"
          disabled={pwLoading}
          className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
        >
          {pwLoading ? "Updating..." : "Update password"}
        </button>
      </form>

      <div className="grid gap-4 md:grid-cols-2">
        <InfoCard
          title={t("settings.deviceIdLabel", "Device ID")}
          value={deviceId ?? t("settings.notRegistered", "Not registered yet")}
          description={t("settings.deviceIdDesc", "Fingerprint stored with each scan for audit compliance.")}
        />
        <InfoCard
          title={t("settings.browserLabel", "Browser")}
          value={navigator.userAgent}
          description={t("settings.browserDesc", "UA recorded to monitor device integrity.")}
        />
        <InfoCard
          title={t("settings.cameraPermission", "Camera permission")}
          value={formatPermission(cameraAllowed, t)}
          description={t("settings.cameraDesc", "Required to scan QR tags. Adjust in system settings if denied.")}
        />
        <InfoCard
          title={t("settings.locationPermission", "Location permission")}
          value={formatPermission(locationAllowed, t)}
          description={t("settings.locationDesc", "Required for GPS verification on each checkpoint.")}
        />
      </div>


      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800">{t("settings.installPwa", "Install as PWA")}</h2>
        <ol className="mt-2 space-y-2 text-sm text-gray-600">
          <li><strong>{t("settings.pwaIos", "iOS (Safari):")}</strong> {t("settings.pwaIosSteps", "Tap Share → Add to Home Screen.")}</li>
          <li><strong>{t("settings.pwaAndroid", "Android (Chrome):")}</strong> {t("settings.pwaAndroidSteps", "Menu → Install App.")}</li>
          <li><strong>{t("settings.pwaDesktop", "Desktop:")}</strong> {t("settings.pwaDesktopSteps", "Use browser install prompt in the address bar.")}</li>
        </ol>
      </section>
    </section>
  );
}

function InfoCard({
  title,
  value,
  description
}: {
  title: string;
  value: string;
  description: string;
}) {
  return (
    <article className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">{title}</h3>
      <p className="mt-1 text-sm break-words text-gray-800">{value}</p>
      <p className="mt-2 text-xs text-gray-600">{description}</p>
    </article>
  );
}


function formatPermission(state: boolean | null, t: (key: string, fallback?: string) => string) {
  if (state === null) return t("settings.permUnknown", "Unknown");
  return state ? t("settings.permGranted", "Granted") : t("status.denied", "Denied");
}
